import { machinesConstants } from './machines.constants';
import { axiosInstance, ENDPOINT } from '../../common/constants';
import { handleResponse } from '../../common/helpers';
import { getSearchParamFilters } from './utils';


export const machinesActions = {
  setMachineSorter,
  setMachineSearchParams,
  initMachinePaginater,
  setMachinePaginater,
  increaseMachinePaginater,
  getMachines,
  getAreas,
  toggleMachinesRequiringAttention,
  setMachinesRequiringAttention,
  setAttentionMachineViewed,
  unsetAttentionMachineViewed,
  getAttentionSummary,
  getAttentionInsights,
  snoozeMachine,
  unsnoozeMachine,
  bulkSnoozeMachines,
  bulkMarkReviewed,
  setMachineWatched,
  bulkAddToWatchlist
};

// Snooze removes the asset from the attention queue (unless include_snoozed),
// so refresh the list + summary after the mutation rather than optimistic.
function snoozeMachine(machineId, days) {
  return dispatch =>
    handleResponse(axiosInstance.post(ENDPOINT.SNOOZE_MACHINE(machineId), { days }))
      .then(() => {
        dispatch({ type: machinesConstants.REMOVE_MACHINES, machine_ids: [machineId] });
        dispatch(getAttentionSummary());
      });
}

function unsnoozeMachine(machineId) {
  return dispatch =>
    handleResponse(axiosInstance.delete(ENDPOINT.SNOOZE_MACHINE(machineId)))
      .then(() => {
        dispatch(getMachines());
        dispatch(getAttentionSummary());
      });
}

function bulkSnoozeMachines(machineIds, days) {
  return dispatch =>
    handleResponse(axiosInstance.post(ENDPOINT.SNOOZE_MACHINES, { machine_ids: machineIds, days }))
      .then(() => {
        dispatch({ type: machinesConstants.REMOVE_MACHINES, machine_ids: machineIds });
        dispatch(getAttentionSummary());
      });
}

function bulkMarkReviewed(machineIds) {
  return (dispatch) => {
    machineIds.forEach(id =>
      dispatch({ type: machinesConstants.SET_MACHINE_REVIEWED, machine_id: id, reviewed: true }));
    return handleResponse(axiosInstance.patch(ENDPOINT.BULK_MARK_ATTENTION_READ, { machine_ids: machineIds }))
      .then(
        () => dispatch(getAttentionSummary()),
        () => {
          machineIds.forEach(id =>
            dispatch({ type: machinesConstants.SET_MACHINE_REVIEWED, machine_id: id, reviewed: false }));
        }
      );
  };
}

function setMachineWatched(machineId, watched) {
  return (dispatch) => {
    dispatch({ type: machinesConstants.SET_MACHINE_WATCHED, machine_id: machineId, watched });
    const request = watched
      ? axiosInstance.put(ENDPOINT.WATCHLIST_ITEM(machineId))
      : axiosInstance.delete(ENDPOINT.WATCHLIST_ITEM(machineId));
    return handleResponse(request).then(
      (res) => {
        dispatch(getAttentionSummary());
        return res;
      },
      () => dispatch({ type: machinesConstants.SET_MACHINE_WATCHED, machine_id: machineId, watched: !watched })
    );
  };
}

function bulkAddToWatchlist(machineIds) {
  return (dispatch, getState) => {
    const items = ((getState().machines.items || {}).object) || [];
    const previousWatchedById = machineIds.reduce((acc, id) => {
      const item = items.find(machine => machine.id === id);
      acc[id] = Boolean(item && item.is_watched);
      return acc;
    }, {});

    machineIds.forEach(id =>
      dispatch({ type: machinesConstants.SET_MACHINE_WATCHED, machine_id: id, watched: true }));
    return handleResponse(axiosInstance.get(ENDPOINT.WATCHLIST))
      .then((res) => {
        const existing = (res && res.machine_ids) || [];
        const merged = [...new Set([...existing, ...machineIds])];
        return handleResponse(axiosInstance.put(ENDPOINT.WATCHLIST, { machine_ids: merged }));
      })
      .then(
        () => dispatch(getAttentionSummary()),
        () => {
          machineIds.forEach(id =>
            dispatch({ type: machinesConstants.SET_MACHINE_WATCHED, machine_id: id, watched: previousWatchedById[id] }));
        }
      );
  };
}

// Insights drawer KPIs/widgets — the metrics-enabled (heavier) summary call,
// fetched lazily only when the drawer is open. `trendWindow` is { unit, value }.
function getAttentionInsights(trendWindow, machinesRequiringAttention) {
  const request = () => ({ type: machinesConstants.GET_ATTENTION_INSIGHTS_REQUEST });
  const success = data => ({ type: machinesConstants.GET_ATTENTION_INSIGHTS_SUCCESS, data });
  const failure = error => ({ type: machinesConstants.GET_ATTENTION_INSIGHTS_FAILURE, error });

  return (dispatch, getState) => {
    const machinesState = getState().machines;
    const { searchParams } = machinesState;
    const scopedToAttention = machinesRequiringAttention === undefined
      ? Boolean(machinesState.machinesRequiringAttention.show)
      : Boolean(machinesRequiringAttention);
    const filters = getSearchParamFilters({ ...searchParams, attention_criteria: [] });
    const params = {
      machines_requiring_attention: scopedToAttention,
      resource_version: 3,
      search_key: searchParams.searchKey || '',
      include_metrics: true,
      filters
    };
    if (searchParams && searchParams.include_snoozed) {
      params.include_snoozed = true;
    }
    if (trendWindow && trendWindow.unit === 'days') params.trend_period_days = trendWindow.value;
    if (trendWindow && trendWindow.unit === 'months') params.trend_period_months = trendWindow.value;

    dispatch(request());
    return handleResponse(axiosInstance.get(ENDPOINT.GET_ATTENTION_SUMMARY, { params }))
      .then(
        (res) => { dispatch(success(res)); return res; },
        (error) => { dispatch(failure(error)); }
      );
  };
}

function getAttentionSummary() {
  const request = () => ({ type: machinesConstants.GET_ATTENTION_SUMMARY_REQUEST });
  const success = data => ({ type: machinesConstants.GET_ATTENTION_SUMMARY_SUCCESS, data });
  const failure = error => ({ type: machinesConstants.GET_ATTENTION_SUMMARY_FAILURE, error });

  return (dispatch, getState) => {
    const machinesState = getState().machines;
    const { searchParams } = machinesState;
    // Exclude attention_criteria so the strip shows counts for every criterion
    const filters = getSearchParamFilters({ ...searchParams, attention_criteria: [] });
    const params = {
      machines_requiring_attention: true,
      resource_version: 3,
      search_key: searchParams.searchKey || '',
      filters
    };
    if (searchParams && searchParams.include_snoozed) {
      params.include_snoozed = true;
    }

    dispatch(request());
    return handleResponse(axiosInstance.get(ENDPOINT.GET_ATTENTION_SUMMARY, { params }))
      .then(
        (res) => { dispatch(success(res)); return res; },
        (error) => { dispatch(failure(error)); }
      );
  };
}

function setMachineSorter(sorter) {
  return (dispatch) => {
    dispatch({ type: machinesConstants.SET_MACHINE_SORTER, sorter });
    dispatch({ type: machinesConstants.CLEAR_MACHINES });
    dispatch({ type: machinesConstants.SET_MACHINE_PAGINATER, page: 1 });

    dispatch(getMachines());
  };
}

function toggleMachinesRequiringAttention() {
  return (dispatch, getState) => {
    const wasShowing = getState().machines.machinesRequiringAttention.show;
    const enteringAttention = !wasShowing;
    dispatch({ type: machinesConstants.SET_MACHINES_REQUIRING_ATTENTION_VIEW });
    if (enteringAttention) {
      dispatch({ type: machinesConstants.SET_MACHINE_SORTER, sorter: {} });
    }
    dispatch({ type: machinesConstants.CLEAR_MACHINES });
    dispatch({ type: machinesConstants.SET_MACHINE_PAGINATER, page: 1 });

    dispatch(getMachines());
  };
}

function setMachinesRequiringAttention(show, refresh = true) {
  return (dispatch, getState) => {
    const wasShowing = getState().machines.machinesRequiringAttention.show;
    const enteringAttention = show && !wasShowing;
    dispatch({ type: machinesConstants.SET_MACHINES_REQUIRING_ATTENTION_VIEW, show });
    if (enteringAttention) {
      dispatch({ type: machinesConstants.SET_MACHINE_SORTER, sorter: {} });
    }
    dispatch({ type: machinesConstants.CLEAR_MACHINES });
    dispatch({ type: machinesConstants.SET_MACHINE_PAGINATER, page: 1 });

    if (refresh) {
      dispatch(getMachines());
    }
  };
}

function initMachinePaginater() {
  return (dispatch) => {
    dispatch({ type: machinesConstants.CLEAR_MACHINES });
    dispatch({ type: machinesConstants.CLEAR_MACHINE_SEARCH_PARAMS });
    dispatch(setMachinePaginater(1));
  };
}

function setMachinePaginater(page) {
  return (dispatch) => {
    dispatch({ type: machinesConstants.SET_MACHINE_PAGINATER, page });
    dispatch(getMachines());
  };
}

function setMachineSearchParams(searchParams) {
  return (dispatch) => {
    dispatch({ type: machinesConstants.SET_MACHINE_SEARCH_PARAMS, searchParams });
    dispatch({ type: machinesConstants.CLEAR_MACHINES });
    dispatch({ type: machinesConstants.SET_MACHINE_PAGINATER, page: 1 });
    return dispatch(getMachines());
  };
}

function increaseMachinePaginater() {
  return (dispatch, getState) => {
    const machinesState = getState().machines;
    const { paginater } = machinesState;

    dispatch(setMachinePaginater(paginater.page + 1));
  };
}

function getAreas() {
  const success = areas => ({ type: machinesConstants.GET_AREAS_SUCCESS, areas });
  const failure = error => ({ type: machinesConstants.GET_AREAS_FAILURE, error });

  return (dispatch) => {
    const params = {
      resource_type: 'area'
    };

    return handleResponse(axiosInstance.get(ENDPOINT.GET_FILTER_RESOURCES, { params }))
      .then(
        (res) => {
          dispatch(success(res.result));
          return res;
        },
        (error) => {
          dispatch(failure(error));
          throw error;
        }
      );
  };
}

export function getMachines() {
  const request = page => ({ type: machinesConstants.GET_MACHINES_REQUEST, page });
  const success = (machines, page, total_count) => ({ type: machinesConstants.GET_MACHINES_SUCCESS, machines, page, total_count });
  const failure = error => ({ type: machinesConstants.GET_MACHINES_FAILURE, error });

  return (dispatch, getState) => {
    const machinesState = getState().machines;
    const prevSorter = machinesState.sorter;
    const { paginater } = machinesState;
    const { searchParams } = machinesState;
    const machinesRequiringAttention = machinesState.machinesRequiringAttention.show;
    const page = paginater.page || 1;
    dispatch(request(page));

    const order_by = { field: 'name', direction: 'asc' };
    if (prevSorter && prevSorter.sorter && prevSorter.sorter.order) {
      order_by.field = prevSorter.sorter.name;
      order_by.direction = prevSorter.sorter.order;
    }

    const params = {
      page,
      per_page: machinesConstants.NUMBER_OF_MACHINES_PER_PAGE,
      search_key: searchParams.searchKey,
      machines_requiring_attention: machinesRequiringAttention,
      resource_version: 3
    };

    if (searchParams && searchParams.include_snoozed) {
      params.include_snoozed = true;
    }

    if (searchParams) {
      params.filters = getSearchParamFilters(searchParams);
    }

    if (!machinesRequiringAttention || (
      machinesRequiringAttention && prevSorter && prevSorter.sorter && prevSorter.sorter.order
    )) {
      params.order_by = order_by;
    }

    return handleResponse(axiosInstance.get(ENDPOINT.GET_MACHINE_LIST, { params }))
      .then(
        (res) => {
          dispatch(success(res.items, page, res.total_count));
        },
        (error) => {
          dispatch(failure(error));
        },
      );
  };
}

function setAttentionMachineViewed(machine_id) {
  return (dispatch) => {
    dispatch({ type: machinesConstants.SET_MACHINE_REVIEWED, machine_id, reviewed: true });
    return handleResponse(axiosInstance.patch(ENDPOINT.UPDATE_ATTENTION_MACHINE_READ(machine_id)))
      .then(
        res => res,
        () => {
          // rollback optimistic update on failure
          dispatch({ type: machinesConstants.SET_MACHINE_REVIEWED, machine_id, reviewed: false });
        },
      );
  };
}

function unsetAttentionMachineViewed(machine_id) {
  return (dispatch) => {
    dispatch({ type: machinesConstants.SET_MACHINE_REVIEWED, machine_id, reviewed: false });
    return handleResponse(axiosInstance.delete(ENDPOINT.UPDATE_ATTENTION_MACHINE_READ(machine_id)))
      .then(
        res => res,
        () => {
          dispatch({ type: machinesConstants.SET_MACHINE_REVIEWED, machine_id, reviewed: true });
        },
      );
  };
}
